import { useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { X, SpeakerSimpleHigh, SpeakerSimpleSlash } from "@phosphor-icons/react";

function FullscreenPlayer({ src, onClose, testId }) {
  const ref = useRef(null);
  useEffect(() => {
    const v = ref.current;
    v.muted = false;
    v.play().catch(() => {});
    const onKey = (e) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);
  return createPortal(
    <div data-testid={`${testId}-fullscreen`} className="fixed inset-0 z-[90] bg-black flex items-center justify-center" onClick={onClose}>
      <video ref={ref} src={src} controls autoPlay loop playsInline className="max-w-full max-h-full w-full h-full object-contain"
        onClick={(e) => e.stopPropagation()} />
      <button type="button" data-testid={`${testId}-fullscreen-close`} aria-label="Close video" onClick={onClose}
        className="absolute top-4 right-4 h-11 w-11 rounded-full bg-white/15 backdrop-blur text-white flex items-center justify-center hover:bg-white/25 transition-colors">
        <X size={22} weight="bold" />
      </button>
    </div>,
    document.body,
  );
}

// Plays while at least 60% on screen, pauses otherwise. Starts muted unless `soundOn`;
// the speaker button toggles sound, a tap on the video opens it full screen with sound.
export function InViewVideo({ src, soundOn = false, className, testId = "inview-video" }) {
  const ref = useRef(null);
  const openRef = useRef(false);
  const mutedRef = useRef(!soundOn);
  const [open, setOpen] = useState(false);
  const [muted, setMuted] = useState(!soundOn);

  useEffect(() => {
    const v = ref.current;
    if (!v) return undefined;
    const io = new IntersectionObserver(([e]) => {
      if (!openRef.current && e.isIntersecting && e.intersectionRatio >= 0.6) {
        v.muted = mutedRef.current;
        v.play().catch(() => {
          v.muted = true;
          mutedRef.current = true;
          setMuted(true);
          v.play().catch(() => {});
        });
      } else {
        v.pause();
      }
    }, { threshold: [0, 0.6, 1] });
    io.observe(v);
    return () => io.disconnect();
  }, []);

  const toggleSound = (e) => {
    e.stopPropagation();
    const v = ref.current;
    const next = !mutedRef.current;
    mutedRef.current = next;
    setMuted(next);
    v.muted = next;
    if (!next) v.play().catch(() => {});
  };
  const show = (e) => {
    e.stopPropagation();
    openRef.current = true;
    ref.current?.pause();
    setOpen(true);
  };
  const close = useCallback(() => {
    openRef.current = false;
    setOpen(false);
    ref.current?.play().catch(() => {});
  }, []);

  return (
    <>
      <span className="relative inline-block shrink-0">
        <video ref={ref} src={src} muted loop playsInline preload="auto" onClick={show}
          className={`${className} cursor-pointer`} data-testid={testId} />
        <button type="button" data-testid={`${testId}-sound`} aria-label={muted ? "Turn sound on" : "Turn sound off"} onClick={toggleSound}
          className="absolute bottom-2 right-2 h-8 w-8 rounded-full bg-black/55 text-white flex items-center justify-center backdrop-blur hover:bg-black/70 transition-colors">
          {muted ? <SpeakerSimpleSlash size={16} weight="fill" /> : <SpeakerSimpleHigh size={16} weight="fill" />}
        </button>
      </span>
      {open && <FullscreenPlayer src={src} onClose={close} testId={testId} />}
    </>
  );
}
